import {
  charsFontWidth12pixelsSize400Map,
  charsFontWidth14pixelsSize400Map,
  charsFontWidth16pixelsSize500Map,
  charsFontWidth18pixelsSize400Map,
  type FontWidthMap,
} from '../components/text-ellipsis/chars-font-width-map.js';
import { calculateWidth } from '../components/text-ellipsis/truncate-string.js';
import { AUTO_TEXT_SIZE_PRESETS } from '../constants.js';
import type { TextSize } from '../types/text-size.js';
import { getFontDetails } from './get-font-details.js';

/**
 * A function that measures the pixel width of a string at a given font size.
 * @internal
 */
export type TextMeasurer = (text: string, fontSize: number) => number;

/**
 * Resolves the {@link FontWidthMap} to use when measuring at a given font
 * size. Charts supply their own resolver because the appropriate weight (and
 * therefore the appropriate map) depends on the chart-specific typography.
 * @internal
 */
export type FontWidthMapResolver = (fontSize: number) => FontWidthMap;

/**
 * Options for {@link getTextMeasurer}.
 * @internal
 */
export interface GetTextMeasurerOptions {
  /** Font weight applied when falling back to canvas measurement. */
  fontWeight: number;
  /** Resolves which lookup map to use for preset/auto sizes. */
  resolveMap?: FontWidthMapResolver;
}

// Note: the 16px entry uses the 500-weight map regardless of the fontWeight option.
// Callers rendering 16px text at weight 400 (e.g. labelMeasure when textSize=20)
// will slightly over-estimate widths, causing earlier-than-necessary truncation.
// Pass a custom resolveMap to getTextMeasurer to avoid this.
const defaultResolveMap: FontWidthMapResolver = (fontSize) => {
  switch (fontSize) {
    case 18:
      return charsFontWidth18pixelsSize400Map;
    case 16:
      return charsFontWidth16pixelsSize500Map;
    case 14:
      return charsFontWidth14pixelsSize400Map;
    default:
      return charsFontWidth12pixelsSize400Map;
  }
};

/**
 * Returns the appropriate {@link TextMeasurer} for the given text size.
 *
 * When `textSize` is `'auto'` or one of the preset font sizes defined in
 * {@link AUTO_TEXT_SIZE_PRESETS}, a fast lookup-table measurer is returned.
 * Otherwise a measurer backed by `CanvasRenderingContext2D.measureText` is
 * returned for accurate results with arbitrary font sizes.
 * @internal
 */
export const getTextMeasurer = (
  ctx: CanvasRenderingContext2D,
  textSize: TextSize,
  options: GetTextMeasurerOptions,
): TextMeasurer => {
  const { fontWeight, resolveMap = defaultResolveMap } = options;

  if (textSize === 'auto' || isPresetFontSize(textSize)) {
    return (text, fontSize) => calculateWidth(text, resolveMap(fontSize));
  }

  return getCanvasContextMeasurer(ctx, fontWeight);
};

const isPresetFontSize = (fontSize: number) =>
  AUTO_TEXT_SIZE_PRESETS.includes(fontSize);

const getCanvasContextMeasurer =
  (ctx: CanvasRenderingContext2D, fontWeight: number): TextMeasurer =>
  (text, fontSize) => {
    const lineHeight = fontSize * 1.25;
    ctx.font = getFontDetails(fontWeight, fontSize, lineHeight);

    return ctx.measureText(text).width;
  };
